import type { ScenarioViewLens } from './scenarioLens.types';

export const sortLensesByDisplayOrder = (lenses: ScenarioViewLens[]): ScenarioViewLens[] =>
    [...lenses].sort((left, right) => left.display_order - right.display_order);

export const normalizeLensDisplayOrder = (lenses: ScenarioViewLens[]): ScenarioViewLens[] =>
    sortLensesByDisplayOrder(lenses).map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));

export const toggleLensSelection = (
    lenses: ScenarioViewLens[],
    lensId: number,
    isSelected: boolean,
): ScenarioViewLens[] =>
    lenses.map((lens) => (lens.lens_id === lensId ? { ...lens, is_selected: isSelected } : lens));

/** Selected lenses first, then unselected — preserves relative order within each group. */
export const reorderLensesSelectedFirst = (lenses: ScenarioViewLens[]): ScenarioViewLens[] => {
    const orderedLenses = sortLensesByDisplayOrder(lenses);
    const selectedLenses = orderedLenses.filter((lens) => lens.is_selected);
    const unselectedLenses = orderedLenses.filter((lens) => !lens.is_selected);

    return [...selectedLenses, ...unselectedLenses].map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));
};

/** When selecting via catalog, append to the end of the draggable group; unselected lenses sink below. */
export const reorderLensOnCatalogSelectionChange = (
    lenses: ScenarioViewLens[],
    lensId: number,
    isSelected: boolean,
): ScenarioViewLens[] => {
    const updatedLenses = toggleLensSelection(lenses, lensId, isSelected);
    const orderedLenses = sortLensesByDisplayOrder(updatedLenses);

    if (!isSelected) {
        return reorderLensesSelectedFirst(updatedLenses);
    }

    const selectedLenses = orderedLenses.filter((lens) => lens.is_selected && lens.lens_id !== lensId);
    const unselectedLenses = orderedLenses.filter((lens) => !lens.is_selected);
    const targetLens = orderedLenses.find((lens) => lens.lens_id === lensId);
    if (!targetLens) {
        return reorderLensesSelectedFirst(updatedLenses);
    }

    return [...selectedLenses, targetLens, ...unselectedLenses].map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));
};
